/**
 * Report Data Context - Single Source of Truth
 * 
 * CORE RULES:
 * 1. One uploaded report = one saved structured dataset = one consistent truth
 * 2. All pages read from this central store for report-derived values
 * 3. Clear separation of: explicit (parsed), derived (calculated), workflow (system)
 * 4. No fallback to mock/demo data once real report exists
 * 5. Cross-page consistency validation built-in
 * 
 * SOURCE REPORT SELECTION:
 * - Latest report is auto-preselected for convenience
 * - User can switch to an older report
 * - Selected report is LOCKED for the dispute round
 * - No silent switching back to latest after selection
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useSupportContext } from './SupportContext';
import { cachedFetch, fetchCacheKey } from '../utils/fetchCache';
import { tradelineMatchesBureauFilter } from '../utils/tradelineBureaus';
import {
  deriveTradelineIsChargeOff,
  deriveTradelineIsCollection,
} from '../utils/paymentHistoryUtils';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Value source types
export const VALUE_SOURCE = {
  EXPLICIT: 'explicit',       // Direct from parsed report
  DERIVED: 'derived',         // Calculated from explicit values
  WORKFLOW: 'workflow',       // System/workflow state (disputes, etc.)
  NO_DATA: 'no_data',         // Missing from report
  FALLBACK: 'fallback',       // Should NOT exist if real report exists
};

const extractBureauScore = (bureauData) => {
  if (!bureauData || typeof bureauData !== 'object') return null;
  const raw = bureauData.score ?? bureauData.credit_score;
  if (raw == null) return null;
  if (typeof raw === 'object') return raw.value ?? null;
  return typeof raw === 'number' ? raw : null;
};

const buildReportSummaryFromData = (report, isLatest = true) => {
  if (!report?.report_id) return null;
  const provider =
    report.import_provider ||
    report.provider_name ||
    report.provider ||
    report.import_source ||
    report.source;
  const createdAt = report.created_at || report.imported_at || report.updated_at;
  const displayProvider = provider
    ? String(provider).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : null;

  return {
    report_id: report.report_id,
    created_at: createdAt,
    file_name: report.file_name || (displayProvider ? `Credit Report — ${displayProvider}` : 'Credit Report'),
    source: report.import_source || report.source || 'import',
    scores: {
      transunion: extractBureauScore(report.transunion),
      experian: extractBureauScore(report.experian),
      equifax: extractBureauScore(report.equifax),
    },
    is_latest: isLatest,
    label: isLatest ? 'Latest report (recommended)' : null,
  };
};

const normalizeReportListItem = (report, index = 0) => {
  if (!report?.report_id) return null;

  const provider = report.provider || report.import_provider || report.provider_name;
  const createdAt = report.created_at || report.imported_at || report.updated_at;
  const displayDate = report.display_date;
  const fileName =
    report.file_name ||
    (displayDate && provider ? `${displayDate} — ${provider}` : null) ||
    (provider ? `Credit Report — ${provider}` : 'Credit Report');

  const scores = report.scores || {
    transunion: extractBureauScore(report.transunion),
    experian: extractBureauScore(report.experian),
    equifax: extractBureauScore(report.equifax),
  };

  return {
    report_id: report.report_id,
    created_at: createdAt,
    file_name: fileName,
    source: report.source || report.import_source || 'import',
    scores,
    is_latest: report.is_latest ?? index === 0,
    label: report.label || (report.is_latest ? 'Latest report (recommended)' : null),
  };
};

const logReportContextState = (label, state) => {
  if (process.env.NODE_ENV !== 'development') return;
  console.debug(`[ReportDataContext] ${label}`, state);
};

// Create context
const ReportDataContext = createContext(null);

/**
 * Provider component that manages the single source of truth for report data
 */
export const ReportDataProvider = ({ children }) => {
  const { getAuthHeaders, token, user, sessionReady, isAuthenticated } = useAuth();
  const { isInSupportMode, supportContext, contextReady } = useSupportContext();
  const lastReportScopeKeyRef = useRef(null);
  const isPlatformOwner = user?.role === 'platform_owner' || user?.role === 'super_admin';
  
  // Core state - the single source of truth
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  
  // Report selection state
  const [availableReports, setAvailableReports] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [isReportLocked, setIsReportLocked] = useState(false);
  
  // Consistency validation state
  const [consistencyErrors, setConsistencyErrors] = useState([]);

  const reportScopeKey = useMemo(() => {
    const actorUserId = user?.user_id || '';
    const effectiveUserId = isInSupportMode
      ? (supportContext?.effective_user_id || '')
      : actorUserId;
    return `${actorUserId}:${effectiveUserId}`;
  }, [user?.user_id, isInSupportMode, supportContext?.effective_user_id]);

  /**
   * Support Mode fallback: portal dashboard is support-context aware even when
   * legacy report endpoints were queried with the platform owner identity.
   */
  const fetchSupportModeReportFallback = useCallback(async () => {
    if (!token || !isInSupportMode) return null;

    try {
      const dashRes = await axios.get(`${API}/portal/self-serve/dashboard`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      const dash = dashRes.data || {};
      const reportId = dash.report?.report_id || dash.latest_report_id;
      if (!reportId) return null;

      const fullRes = await axios.get(`${API}/credit-reports/${reportId}/full`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      const payload = fullRes.data || {};
      if (payload.status === 'has_report' && payload.report) {
        return payload.report;
      }
      return payload.report || null;
    } catch (err) {
      console.warn('[ReportDataContext] Support Mode report fallback failed:', err);
      return null;
    }
  }, [token, isInSupportMode, getAuthHeaders]);

  /**
   * Fetch the complete report data from the backend
   * This is the ONLY place that should fetch report data
   */
  const fetchReportData = useCallback(async (force = false, specificReportId = null) => {
    // If a specific report is requested, use that endpoint
    if (specificReportId) {
      return fetchSpecificReport(specificReportId);
    }
    
    // Skip if recently fetched (within 30 seconds) unless forced
    if (!force && lastFetched && (Date.now() - lastFetched) < 30000) {
      return reportData;
    }

    if (!isAuthenticated && !token) {
      setLoading(false);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch the complete report data from a single endpoint
      const response = await axios.get(`${API}/report-data`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      const data = response.data || {};
      let report =
        (data.status === 'has_report' && data.report) ? data.report
        : (data.report_id || data.transunion || data.experian || data.equifax) ? data
        : null;

      if (!report && isInSupportMode) {
        report = await fetchSupportModeReportFallback();
      }
      
      if (report) {
        // Store the report data directly from API - it's already well-structured
        // The API returns: transunion, experian, equifax at top level with all data
        setReportData(report);
        setLastFetched(Date.now());
        
        // Set the selected report ID if not already locked
        if (!isReportLocked && report.report_id) {
          setSelectedReportId(report.report_id);
        }

        // If list endpoints returned empty, synthesize from active report-data
        setAvailableReports((prev) => {
          if (prev.some((entry) => entry.report_id === report.report_id)) {
            return prev;
          }
          const summary = buildReportSummaryFromData(report, prev.length === 0);
          return summary ? (prev.length === 0 ? [summary] : [...prev, summary]) : prev;
        });
        
        // Run consistency validation
        validateConsistency(report);

        logReportContextState('fetchReportData complete', {
          reportDataExists: true,
          reportId: report.report_id,
          bureauKeys: ['transunion', 'experian', 'equifax'].filter((b) => Boolean(report[b])),
          negativeAccounts: (report.tradelines || []).filter((t) => t.is_negative).length,
          inquiryCount: (report.inquiries || []).length,
        });
        
        return report;
      } else {
        // No report imported yet
        setReportData(null);
        return null;
      }
    } catch (err) {
      console.error('Failed to fetch report data:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated, getAuthHeaders, lastFetched, reportData, isReportLocked, isInSupportMode, fetchSupportModeReportFallback]);

  /**
   * Fetch a specific report by ID
   * Used when user selects an older report for dispute generation
   */
  const fetchSpecificReport = useCallback(async (reportId) => {
    if ((!isAuthenticated && !token) || !reportId) {
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`${API}/report-data/${reportId}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });

      const data = response.data;
      
      if (data.status === 'has_report' && data.report) {
        const report = data.report;
        setReportData(report);
        setSelectedReportId(reportId);
        setLastFetched(Date.now());
        
        validateConsistency(report);
        return report;
      } else {
        throw new Error(`Report ${reportId} not found`);
      }
    } catch (err) {
      console.error('Failed to fetch specific report:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated, getAuthHeaders]);

  /**
   * Fetch list of available reports for selection
   */
  const fetchAvailableReports = useCallback(async () => {
    if (!isAuthenticated && !token) return [];

    setListLoading(true);
    try {
      const cacheKey = fetchCacheKey('credit-reports-history', reportScopeKey);
      const reports = await cachedFetch(
        cacheKey,
        async () => {
          const headers = getAuthHeaders();
          let nextReports = [];

          try {
            const historyResponse = await axios.get(`${API}/credit-reports/history`, {
              headers,
              withCredentials: true,
            });
            const historyPayload = historyResponse.data || {};
            const historyReports = Array.isArray(historyPayload.reports) ? historyPayload.reports : [];
            nextReports = historyReports
              .map((report, index) => normalizeReportListItem(report, index))
              .filter(Boolean);
          } catch (historyErr) {
            console.warn('Failed to fetch credit-reports/history, falling back to report-list:', historyErr);
          }

          if (nextReports.length === 0) {
            const response = await axios.get(`${API}/report-list`, {
              headers,
              withCredentials: true,
            });
            const payload = response.data || {};
            const rawReports = Array.isArray(payload.reports)
              ? payload.reports
              : Array.isArray(payload)
                ? payload
                : [];
            nextReports = rawReports
              .map((report, index) => normalizeReportListItem(report, index))
              .filter(Boolean);
          }

          if (nextReports.length === 0 && isInSupportMode) {
            const fallbackReport = await fetchSupportModeReportFallback();
            const summary = buildReportSummaryFromData(fallbackReport, true);
            if (summary) {
              nextReports = [summary];
            }
          }

          return nextReports;
        },
        { ttlMs: 20000 }
      );

      setAvailableReports(reports);

      logReportContextState('fetchAvailableReports complete', {
        availableReportsLength: reports.length,
        selectedReportId,
        reportIds: reports.map((r) => r.report_id),
      });

      return reports;
    } catch (err) {
      console.error('Failed to fetch report list:', err);
      return [];
    } finally {
      setListLoading(false);
    }
  }, [token, isAuthenticated, getAuthHeaders, selectedReportId, isInSupportMode, fetchSupportModeReportFallback, reportScopeKey]);

  /**
   * Select a report for the dispute round
   * This preselects the report but doesn't lock it yet
   */
  const selectReport = useCallback(async (reportId) => {
    if (!reportId) return null;
    
    // If locked, don't allow switching
    if (isReportLocked) {
      console.warn('Report is locked for this round. Cannot switch.');
      return reportData;
    }

    if (
      reportId === selectedReportId &&
      reportData?.report_id === reportId &&
      lastFetched &&
      Date.now() - lastFetched < 30000
    ) {
      return reportData;
    }
    
    return fetchSpecificReport(reportId);
  }, [isReportLocked, reportData, fetchSpecificReport, selectedReportId, lastFetched]);

  /**
   * Lock the selected report for the dispute round
   * After this, the report cannot be changed without explicitly unlocking
   */
  const lockSelectedReport = useCallback(() => {
    if (!selectedReportId) {
      console.warn('No report selected to lock');
      return false;
    }
    setIsReportLocked(true);
    console.log(`[REPORT LOCK] Locked report ${selectedReportId} for dispute round`);
    return true;
  }, [selectedReportId]);

  /**
   * Unlock the report to allow switching (requires explicit user action)
   */
  const unlockReport = useCallback(() => {
    setIsReportLocked(false);
    console.log('[REPORT LOCK] Report unlocked - user can now select a different report');
  }, []);

  /**
   * Get the locked report info for display
   */
  const getLockedReportInfo = useCallback(() => {
    if (!isReportLocked || !selectedReportId) return null;
    
    const report = availableReports.find(r => r.report_id === selectedReportId);
    return {
      report_id: selectedReportId,
      is_locked: true,
      file_name: report?.file_name || reportData?.file_name || 'Selected Report',
      created_at: report?.created_at || reportData?.created_at || reportData?.imported_at,
      is_latest: report?.is_latest ?? true,
    };
  }, [isReportLocked, selectedReportId, availableReports, reportData]);

  /**
   * Normalize report data to ensure consistent structure
   */
  const normalizeReportData = (report) => {
    return {
      // Metadata
      report_id: report.report_id,
      user_id: report.user_id,
      import_source: report.import_source,
      import_provider: report.import_provider,
      created_at: report.created_at,
      updated_at: report.updated_at,

      // EXPLICIT VALUES - Direct from parsed report
      explicit: {
        // Bureau scores
        bureaus: {
          transunion: {
            score: report.transunion?.score ?? null,
            score_source: report.transunion?.score_source ?? VALUE_SOURCE.NO_DATA,
            total_accounts: report.transunion?.total_accounts ?? null,
            open_accounts: report.transunion?.open_accounts ?? null,
            closed_accounts: report.transunion?.closed_accounts ?? null,
            derogatory_count: report.transunion?.derogatory_count ?? report.transunion?.negative_count ?? null,
            collections_count: report.transunion?.collections_count ?? null,
            inquiries_count: report.transunion?.inquiries_count ?? null,
            public_records_count: report.transunion?.public_records_count ?? null,
          },
          experian: {
            score: report.experian?.score ?? null,
            score_source: report.experian?.score_source ?? VALUE_SOURCE.NO_DATA,
            total_accounts: report.experian?.total_accounts ?? null,
            open_accounts: report.experian?.open_accounts ?? null,
            closed_accounts: report.experian?.closed_accounts ?? null,
            derogatory_count: report.experian?.derogatory_count ?? report.experian?.negative_count ?? null,
            collections_count: report.experian?.collections_count ?? null,
            inquiries_count: report.experian?.inquiries_count ?? null,
            public_records_count: report.experian?.public_records_count ?? null,
          },
          equifax: {
            score: report.equifax?.score ?? null,
            score_source: report.equifax?.score_source ?? VALUE_SOURCE.NO_DATA,
            total_accounts: report.equifax?.total_accounts ?? null,
            open_accounts: report.equifax?.open_accounts ?? null,
            closed_accounts: report.equifax?.closed_accounts ?? null,
            derogatory_count: report.equifax?.derogatory_count ?? report.equifax?.negative_count ?? null,
            collections_count: report.equifax?.collections_count ?? null,
            inquiries_count: report.equifax?.inquiries_count ?? null,
            public_records_count: report.equifax?.public_records_count ?? null,
          },
        },

        // Personal information
        personal_info: {
          name: report.personal_info?.name ?? null,
          date_of_birth: report.personal_info?.date_of_birth ?? null,
          ssn_last4: report.personal_info?.ssn_last4 ?? null,
          aliases: report.personal_info?.aliases ?? [],
        },

        // Addresses
        addresses: report.addresses ?? [],

        // Employment
        employment: report.employment ?? [],

        // Tradelines / Accounts
        tradelines: (report.tradelines || []).map(t => ({
          tradeline_id: t.tradeline_id,
          creditor_name: t.creditor_name,
          account_number: t.masked_account_number || t.account_number,
          account_number_masked: t.masked_account_number || t.account_number,  // Also provide as account_number_masked for compatibility
          masked_account_number: t.masked_account_number || t.account_number,  // Original field name
          account_type: t.account_type,
          account_status: t.account_status || t.status,
          status: t.status,
          payment_status: t.payment_status,
          balance: t.balance,
          past_due: t.past_due,
          limit: t.limit,
          credit_limit: t.credit_limit || t.limit,
          high_credit: t.high_credit,
          date_opened: t.date_opened,
          date_reported: t.date_reported,
          date_last_payment: t.date_last_payment,
          date_last_active: t.date_last_active,
          dofd: t.dofd || t.date_of_first_delinquency,
          is_negative: t.is_negative ?? false,
          // Scan ALL status/rating fields — never `status || payment_status`
          // (Closed would mask Metro2 "Collection Or Charge Off").
          is_collection: deriveTradelineIsCollection(t),
          is_charge_off: deriveTradelineIsChargeOff(t),
          reported_bureaus: t.reported_bureaus || t.reportedBureaus || t.bureaus_reported || t.bureausReported || [],
          bureaus_reported: t.bureaus_reported || t.bureausReported || t.reported_bureaus || t.reportedBureaus || [t.bureau].filter(Boolean),
          bureau_data: t.bureau_data || t.bureauData || {},  // Include bureau_data for field-level comparison
          raw_text: t.raw_text,
          source: VALUE_SOURCE.EXPLICIT,
        })),

        // Inquiries
        inquiries: report.inquiries ?? [],

        // Public Records
        public_records: report.public_records ?? [],

        // Creditor Contacts (for dispute letters)
        creditor_contacts: report.creditor_contacts ?? [],
      },

      // DERIVED VALUES - Calculated from explicit values
      derived: {
        // Combined/average score
        average_score: calculateAverageScore(report),
        average_score_formula: '(TransUnion + Experian + Equifax) ÷ 3',

        // Combined totals
        total_accounts: sumAcrossBureaus(report, 'total_accounts'),
        total_open_accounts: sumAcrossBureaus(report, 'open_accounts'),
        total_closed_accounts: sumAcrossBureaus(report, 'closed_accounts'),
        total_negative_items: sumAcrossBureaus(report, 'derogatory_count') || sumAcrossBureaus(report, 'negative_count'),
        total_collections: sumAcrossBureaus(report, 'collections_count'),
        total_inquiries: sumAcrossBureaus(report, 'inquiries_count'),
        total_public_records: sumAcrossBureaus(report, 'public_records_count'),

        // Tradeline counts
        tradeline_count: report.tradelines?.length ?? 0,
        negative_tradeline_count: report.tradelines?.filter(t => t.is_negative).length ?? 0,
        positive_tradeline_count: report.tradelines?.filter(t => !t.is_negative).length ?? 0,
      },

      // AI Analysis (derived from report but generated by AI)
      ai_analysis: {
        summary: report.ai_summary ?? null,
        alerts: report.ai_alerts ?? [],
        recommendations: report.ai_recommendations ?? [],
        next_actions: report.next_actions ?? [],
        funding_blockers: report.funding_blockers ?? [],
      },

      // Parsing metadata
      parsing: {
        provider_detected: report.parsing_details?.provider ?? report.import_provider,
        sections_found: report.parsing_details?.sections_found ?? [],
        parsing_warnings: report.parsing_details?.parsing_warnings ?? [],
        value_sources: extractValueSources(report),
      },
    };
  };

  /**
   * Calculate average score from bureau scores
   */
  const calculateAverageScore = (report) => {
    const scores = [
      report.transunion?.score,
      report.experian?.score,
      report.equifax?.score,
    ].filter(s => s != null && typeof s === 'number');

    if (scores.length === 0) return null;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  };

  /**
   * Sum a field across all bureaus
   */
  const sumAcrossBureaus = (report, field) => {
    const values = [
      report.transunion?.[field],
      report.experian?.[field],
      report.equifax?.[field],
    ].filter(v => v != null && typeof v === 'number');

    if (values.length === 0) return null;
    return values.reduce((a, b) => a + b, 0);
  };

  /**
   * Extract value sources for debugging
   */
  const extractValueSources = (report) => {
    const sources = {};
    for (const bureau of ['transunion', 'experian', 'equifax']) {
      sources[bureau] = {
        score: report[bureau]?.score_source ?? report[bureau]?.data_sources?.score ?? VALUE_SOURCE.NO_DATA,
        summary: report[bureau]?.data_sources?.summary ?? VALUE_SOURCE.NO_DATA,
      };
    }
    return sources;
  };

  /**
   * Validate data consistency
   */
  const validateConsistency = (data) => {
    const errors = [];

    if (!data) {
      setConsistencyErrors([]);
      return;
    }

    // Validate bureau scores exist - API returns bureaus at top level
    for (const bureau of ['transunion', 'experian', 'equifax']) {
      const bureauData = data[bureau];
      if (bureauData && bureauData.score === null && bureauData.score_source !== VALUE_SOURCE.NO_DATA) {
        errors.push({
          field: `${bureau}_score`,
          message: `${bureau} score is null but source is not 'no_data'`,
          severity: 'warning',
        });
      }
    }

    // Validate derived values match explicit sum
    const explicitTotal = ['transunion', 'experian', 'equifax']
      .reduce((sum, b) => sum + (data[b]?.total_accounts ?? 0), 0);
    if (data.derived?.total_accounts_combined !== explicitTotal && data.derived?.total_accounts_combined !== null) {
      errors.push({
        field: 'total_accounts',
        message: `Derived total (${data.derived?.total_accounts_combined}) doesn't match sum of bureaus (${explicitTotal})`,
        severity: 'error',
      });
    }

    setConsistencyErrors(errors);
  };

  /**
   * Get bureau-specific data (helper for pages)
   */
  const getBureauData = useCallback((bureau) => {
    if (!reportData) return null;
    const bureauKey = bureau.toLowerCase();
    return reportData[bureauKey] ?? null;
  }, [reportData]);

  /**
   * Get all tradelines with optional filters
   */
  const getTradelines = useCallback((filters = {}) => {
    if (!reportData) return [];
    
    // API returns tradelines at top level
    let tradelines = reportData.tradelines || [];

    // Filter by negative status
    if (filters.negative === true) {
      tradelines = tradelines.filter(t => t.is_negative);
    } else if (filters.negative === false) {
      tradelines = tradelines.filter(t => !t.is_negative);
    }

    // Filter by bureau using reported bureau lists / bureau_data
    if (filters.bureau) {
      tradelines = tradelines.filter((t) => tradelineMatchesBureauFilter(t, filters.bureau));
    }

    // Filter out noise (for display purposes)
    if (filters.excludeNoise !== false) {
      tradelines = tradelines.filter(t => isValidTradeline(t));
    }

    return tradelines;
  }, [reportData]);

  /**
   * Check if a tradeline is valid (not parser noise)
   */
  const isValidTradeline = (tradeline) => {
    const name = tradeline?.creditor_name?.trim() || '';
    if (!name || name.length < 2) return false;
    
    const noisePatterns = [
      /^OK\s+OK/i,
      /^-+$/,
      /^\d+$/,
      /^[A-Z]{1,2}$/,
      /CREDIT\s*REPORT/i,
      /IDENTITYIQ/i,
      /TRANSUNION|EXPERIAN|EQUIFAX/i,
      /^SUMMARY$/i,
      /^ACCOUNT\s*(HISTORY|INFORMATION)?$/i,
      /^INQUIRIES$/i,
      /^PUBLIC\s*RECORDS?$/i,
      /BACK\s*TO\s*TOP/i,
    ];
    
    for (const pattern of noisePatterns) {
      if (pattern.test(name)) return false;
    }
    
    if (!/[a-zA-Z]{2,}/.test(name)) return false;
    return true;
  };

  /**
   * Force refresh the data
   */
  const refreshData = useCallback(() => {
    return fetchReportData(true);
  }, [fetchReportData]);

  /**
   * Clear data (for logout)
   */
  const clearData = useCallback(() => {
    setReportData(null);
    setLastFetched(null);
    setConsistencyErrors([]);
    setError(null);
    setAvailableReports([]);
    setSelectedReportId(null);
    setIsReportLocked(false);
    setLoading(false);
    setListLoading(false);
  }, []);

  // Drop cached report state when actor or effective support user changes (not every render).
  useEffect(() => {
    const previousScopeKey = lastReportScopeKeyRef.current;
    if (previousScopeKey !== null && previousScopeKey !== reportScopeKey) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[ReportDataContext] report scope changed', previousScopeKey, '->', reportScopeKey);
      }
      setReportData(null);
      setAvailableReports([]);
      setSelectedReportId(null);
      setIsReportLocked(false);
      setLastFetched(null);
      setConsistencyErrors([]);
      setError(null);
      setLoading(Boolean(token) || Boolean(isAuthenticated));
      setListLoading(false);
    }
    lastReportScopeKeyRef.current = reportScopeKey;
  }, [reportScopeKey, token, isAuthenticated]);

  // Auto-fetch on mount and when auth or report scope changes
  useEffect(() => {
    if (!isAuthenticated && !token) {
      clearData();
      lastReportScopeKeyRef.current = reportScopeKey;
      return;
    }
    // Platform owners: wait for support context so we don't fetch owner data then refetch client data
    if (isPlatformOwner && !contextReady) {
      return;
    }
    fetchReportData();
    fetchAvailableReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when auth or scoped user identity changes
  }, [token, isAuthenticated, reportScopeKey, contextReady, isPlatformOwner]);

  // Canonical list for consumers: merge list endpoints with active report-data
  const effectiveAvailableReports = useMemo(() => {
    if (availableReports.length > 0) {
      return availableReports;
    }
    const summary = buildReportSummaryFromData(reportData, true);
    return summary ? [summary] : [];
  }, [availableReports, reportData]);

  const hasReport = Boolean(
    reportData?.report_id ||
    effectiveAvailableReports.length > 0
  );

  useEffect(() => {
    if (!selectedReportId && reportData?.report_id && !isReportLocked) {
      setSelectedReportId(reportData.report_id);
    }
  }, [selectedReportId, reportData?.report_id, isReportLocked]);

  useEffect(() => {
    logReportContextState('state snapshot', {
      reportDataExists: Boolean(reportData),
      availableReportsLength: availableReports.length,
      effectiveAvailableReportsLength: effectiveAvailableReports.length,
      selectedReportId: selectedReportId || reportData?.report_id || null,
      bureauKeysPresent: reportData
        ? ['transunion', 'experian', 'equifax'].filter((b) => Boolean(reportData[b]))
        : [],
      negativeAccountsCount: (reportData?.tradelines || []).filter((t) => t.is_negative).length,
      inquiryCount: (reportData?.inquiries || []).length,
      hasReport,
    });
  }, [
    reportData,
    availableReports.length,
    effectiveAvailableReports.length,
    selectedReportId,
    hasReport,
  ]);

  // Build the context value
  const value = useMemo(() => ({
    // State
    reportData,
    loading,
    listLoading,
    error,
    hasReport,
    lastFetched,
    consistencyErrors,
    
    // Report selection state
    availableReports: effectiveAvailableReports,
    selectedReportId: selectedReportId || reportData?.report_id || null,
    isReportLocked,

    // Actions
    fetchReportData,
    refreshData,
    clearData,
    
    // Report selection actions
    fetchAvailableReports,
    selectReport,
    lockSelectedReport,
    unlockReport,
    getLockedReportInfo,

    // Helpers
    getBureauData,
    getTradelines,
    isValidTradeline,

    // Value source constants
    VALUE_SOURCE,

    // Quick access to common values - API returns bureaus at top level
    bureauScores: reportData ? {
      transunion: reportData.transunion?.score,
      experian: reportData.experian?.score,
      equifax: reportData.equifax?.score,
    } : null,
    averageScore: reportData?.derived?.average_score ?? null,
    personalInfo: reportData?.personal_info ?? null,
    // Normalize tradelines to ensure consistent field names
    tradelines: (reportData?.tradelines ?? []).map(t => ({
      ...t,
      // Preserve bureau-specific source data for dispute letter generation
      bureau_data: t.bureau_data || t.bureauData || {},
      payment_history_grid: t.payment_history_grid || {},
      payment_history: t.payment_history || {},
      payment_history_summary: t.payment_history_summary || {},
      // Ensure account_number fields are consistently available
      account_number: t.masked_account_number || t.account_number || '',
      account_number_masked: t.masked_account_number || t.account_number || '',
      masked_account_number: t.masked_account_number || t.account_number || '',
      // Ensure account type is available
      account_type: t.account_type || t.type || '',
      account_type_detail: t.account_type_detail || '',
      // Ensure bureau reporting lists are available without inventing all three
      reported_bureaus: t.reported_bureaus || t.reportedBureaus || t.bureaus_reported || t.bureausReported || [],
      bureaus: t.bureaus_reported || t.bureausReported || t.reported_bureaus || t.reportedBureaus || (t.bureau ? [t.bureau] : []),
      bureaus_reported: t.bureaus_reported || t.bureausReported || t.reported_bureaus || t.reportedBureaus || (t.bureau ? [t.bureau] : []),
    })),
    inquiries: reportData?.inquiries ?? [],
    publicRecords: reportData?.public_records ?? [],
  }), [
    reportData, loading, listLoading, error, hasReport, lastFetched, consistencyErrors,
    effectiveAvailableReports, selectedReportId, isReportLocked,
    fetchReportData, refreshData, clearData, fetchAvailableReports,
    selectReport, lockSelectedReport, unlockReport, getLockedReportInfo,
    getBureauData, getTradelines
  ]);

  return (
    <ReportDataContext.Provider value={value}>
      {children}
    </ReportDataContext.Provider>
  );
};

/**
 * Hook to use report data context
 */
export const useReportData = () => {
  const context = useContext(ReportDataContext);
  if (!context) {
    throw new Error('useReportData must be used within a ReportDataProvider');
  }
  return context;
};

/**
 * Hook for specific bureau data
 */
export const useBureauData = (bureau) => {
  const { getBureauData, loading } = useReportData();
  return { data: getBureauData(bureau), loading };
};

/**
 * Hook for tradelines with filters
 */
export const useTradelines = (filters = {}) => {
  const { getTradelines, loading } = useReportData();
  return { tradelines: getTradelines(filters), loading };
};

export default ReportDataContext;
